import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';
import {useQuery} from '@apollo/client';

import Form from '@appComponents/ReduxFormControls';
import {AllClientPackagesDropdownData} from '@graphql/clientPackages/query';
import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import {GEO_RESTRICTIONS, TEST} from '@constants/staticDropdowns';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import {defineNextState} from '@utils/filters';
import {ProductsFilter} from '@utils/commonFilters/ProductsFilter';
import {ContentVariantsFilter} from '@utils/commonFilters/ContentVariantsFilter';
import {HqFilter} from '@utils/commonFilters/HqFilter';
import {LlFilter} from '@utils/commonFilters/LlFilter';
import {SearchFilter} from '@utils/commonFilters/SearchFilter';
import {ClientsFilter} from '@utils/commonFilters/ClientsFilter';
import {StatusFilter} from '@utils/commonFilters/StatusFilter';
import {DateRangeFilter} from '@utils/commonFilters/DateRangeFilter';
import {buildInitialDatesState} from '@modules/client/utils/clientPackagesHelpers';

const restoreEmptyDateValue = () => null;

export const ClientPackagesFilters = ({
    loading : loadingFilters,
    restoreFilterValues,
    restoreFilterDateValues,
    filters,
    form,
}) => {
    const [dates, setDates] = useState(() => buildInitialDatesState(filters, restoreFilterDateValues));
    const [dateFieldsReset, setDateFieldsReset] = useState(false);
    const filtersContainerRef = useRef(form.changeValue);

    const {
        data: {
            clients,
            products,
            clientPackageTypes,
            bookingTypes,
            contentPackageTypes,
            eventContentVariants,
        } = {},
        loading : areDropdownFiltersLoading = true,
    } = useQuery(AllClientPackagesDropdownData);

    const loading = (areDropdownFiltersLoading || loadingFilters);

    useEffect(() => {
        const filtersForm = filtersContainerRef.current?.closest('form');

        if (!filtersForm) {
            return undefined;
        }

        const resetDateFields = (event) => {
            if (!event.target.closest?.('button[name="ResetButton"]')) {
                return;
            }

            form.changeValue('from', null);
            form.changeValue('to', null);
            setDates({
                fromMaxDate: null,
                fromMaxDateDefault: null,
                fromMaxTime: null,
                toMinDate: null,
                toMinDateDefault: null,
                toMinTime: null,
            });
            setDateFieldsReset(true);
        };

        filtersForm.addEventListener('click', resetDateFields);

        return () => filtersForm.removeEventListener('click', resetDateFields);
    }, [form]);

    const onChange = (date, name) => {
        setDates((prevState) => {
            const nextState = {...prevState};

            defineNextState(nextState, prevState, date, name);

            return nextState;
        });
    };

    return (
        <div className='content__filters' ref={filtersContainerRef}>
            <SearchFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
            />
            <ClientsFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
                options={clients}
            />
            <StatusFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
                statusType='IS_ENABLED'
                fieldName='clientIsDisabled'
                placeholder='Client status'
            />
            <ProductsFilter
                loading={loading}
                products={products}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
            />
            <StatusFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
                statusType='IS_ENABLED'
                fieldName='productIsDisabled'
                placeholder='Product status'
            />
            <Field
                component={Form.ReduxSelect}
                disabled={loading}
                loading={loading}
                name='clientPackageType'
                multiple
                options={sortDropdownOptionsAlphanumerically(clientPackageTypes)}
                placeholder='Package type'
                setValue={restoreFilterValues(filters.ClientPackages, 'clientPackageType')}
            />
            <Field
                component={Form.ReduxSelect}
                disabled={loading}
                loading={loading}
                name='bookingTypes'
                multiple
                options={bookingTypes}
                placeholder='Booking type'
                setValue={restoreFilterValues(filters.ClientPackages,'bookingTypes')}
            />
            <Field
                component={Form.ReduxSelect}
                disabled={loading}
                loading={loading}
                name='contentPackageTypes'
                multiple
                options={sortDropdownOptionsAlphanumerically(contentPackageTypes)}
                placeholder='Content selection'
                setValue={restoreFilterValues(filters.ClientPackages, 'contentPackageTypes')}
            />
            <ContentVariantsFilter
                eventContentVariants={eventContentVariants}
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
            />
            <DateRangeFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterDateValues={dateFieldsReset ? restoreEmptyDateValue : restoreFilterDateValues}
                onChange={onChange}
                showTime={false}
                defaultFromDate={null}
                defaultToDate={null}
                dateConfig={dates}
            />
            <Field
                component={Form.ReduxSelect}
                disabled={loading}
                loading={loading}
                name='geoRestrictions'
                placeholder='Geo restrictions?'
                options={GEO_RESTRICTIONS}
                setValue={restoreFilterValues(filters.ClientPackages, 'geoRestrictions')}
                selection
            />
            <HqFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
            />
            <LlFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
            />
            <Field
                component={Form.ReduxSelect}
                disabled={loading}
                loading={loading}
                name='isTest'
                placeholder='Test?'
                options={TEST}
                setValue={restoreFilterValues(filters.ClientPackages, 'isTest')}
                selection
            />
            <StatusFilter
                loading={loading}
                filters={filters.ClientPackages}
                restoreFilterValues={restoreFilterValues}
                statusType='IS_ENABLED'
                fieldName='isDisabled'
                placeholder='Package status'
            />
        </div>
    );
};

ClientPackagesFilters.propTypes = {
    filters: PropTypes.shape({
        ClientPackages: PropTypes.object.isRequired,
    }),
    loading: PropTypes.bool,
    restoreFilterValues: PropTypes.func.isRequired,
    restoreFilterDateValues: PropTypes.func.isRequired,
    form: PropTypes.shape({
        changeValue: PropTypes.func.isRequired,
    }).isRequired,
};

export default FiltersWrapper(ClientPackagesFilters, {name: 'ClientPackages'});
